import React, { Suspense, lazy } from 'react';
import { useRoutes, Navigate, Outlet } from 'react-router-dom';
import activePages from './activePage';
import PageTransition, { KeepAliveTransition } from '@/components/PageTransition';
import RouteGuard from '@/components/RouteGuard';
import AwakenApp from '@/pages/awaken-app';

const HomeLayout = lazy(() => import('@/pages/home/Layout'));
const Study = lazy(() => import('@/pages/home/Study'));
const Profile = lazy(() => import('@/pages/home/Profile'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PersonalInfo = lazy(() => import('@/pages/personal-info'));
const Login = lazy(() => import('@/pages/login'));
const SmsVerify = lazy(() => import('@/pages/sms-verify'));
const ModifyPwd = lazy(() => import('@/pages/modify-pwd'));
const ChangeNickname = lazy(() => import('@/pages/change-nickname'));
const CallCenter = lazy(() => import('@/pages/call-center'));
const Suggestion = lazy(() => import('@/pages/suggestion'));
const AddressList = lazy(() => import('@/pages/address-list'));
const AddressEdit = lazy(() => import('@/pages/address-edit'));
const Setting = lazy(() => import('@/pages/setting'));
const Protocol = lazy(() => import('@/pages/protocol'));
const CancelAccount = lazy(() => import('@/pages/cancel-account'));
const CancelAccountConfirm = lazy(() => import('@/pages/cancel-account-confirm'));
const CurrentPhone = lazy(() => import('@/pages/current-phone'));
const VerifyCurrentPhone = lazy(() => import('@/pages/verify-current-phone'));
const ModifyPhone = lazy(() => import('@/pages/modify-phone'));
const ForgetPwd = lazy(() => import('@/pages/forget-pwd'));
const RenewClassCoupon = lazy(() => import('@/pages/renew-class-coupon'));
const StudyCalendar = lazy(() => import('@/pages/study-calendar'));
const MyOrder = lazy(() => import('@/pages/my-order'));
const TrialCourse = lazy(() => import('@/pages/trial-course'));
const DownloadApp = lazy(() => import('@/pages/download-app'));
const CourseDetail = lazy(() => import('@/pages/course-detail'));
const PayConfirm = lazy(() => import('@/pages/pay-confirm'));
const LivePlay = lazy(() => import('@/pages/live-play'));
const OrderDetail = lazy(() => import('@/pages/order-detail'));
const Pay = lazy(() => import('@/pages/pay'));
const AliPayMiddle = lazy(() => import('@/pages/ali-pay-middle'));
const PayResult = lazy(() => import('@/pages/pay-result'));
const HelpPay = lazy(() => import('@/pages/help-pay'));
const PackageCourseDetail = lazy(() => import('@/pages/package-course-detail'));
const BookStudy = lazy(() => import('@/pages/book-study-by-self/BookStudy'));
const BookStudySuccess = lazy(() => import('@/pages/book-study-by-self/BookStudySuccess'));
const RefundRecord = lazy(() => import('@/pages/refund-record'));
const Logistics = lazy(() => import('@/pages/logistics-detail'));
const QuestionnaireResult = lazy(() => import('@/pages/app-page/questionnaire-result/index'));

const TieChuHome = lazy(() => import('@/pages/tiechu/Home'));
const TieChuStudentLogin = lazy(() => import('@/pages/tiechu/StudentLogin'));
const TieChuClassroom = lazy(() => import('@/pages/tiechu/Classroom'));
const TieChuTeacher = lazy(() => import('@/pages/tiechu/TeacherDashboard'));
const PrintDeskPresetAudit = lazy(() => import('@/pages/printdesk/preset-audit'));
const PrintDeskFeedbackList = lazy(() => import('@/pages/printdesk/feedback'));
const PrintDeskFeedbackDetail = lazy(() => import('@/pages/printdesk/feedback/detail'));

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? (
      <div style={{ padding: 16 }}>
        加载失败，<a onClick={() => location.reload()}>点击重试</a>
      </div>
    ) : (
      this.props.children
    );
  }
}

// 活动页路由布局组件
const ActivePagesLayout = () => {
  return (
    <div className="ignore-rem-max-width-450">
      <PageTransition>
        <Outlet />
      </PageTransition>
    </div>
  );
};

// 包装组件，为页面添加转场动画
const withPageTransition = (Component: React.ComponentType) => (
  <PageTransition>
    <Component />
  </PageTransition>
);

// 包装KeepAlive组件
const withKeepAliveAndTransition = (Component: React.ComponentType, name: string) => (
  <KeepAliveTransition name={name}>
    <Component />
  </KeepAliveTransition>
);

// 包装需要登录的页面
const withAuth = (Component: React.ComponentType) => (
  <RouteGuard requireAuth={true}>
    <PageTransition>
      <Component />
    </PageTransition>
  </RouteGuard>
);

// 包装需要登录且使用 KeepAlive 的页面
const withAuthAndKeepAlive = (Component: React.ComponentType, name: string) => (
  <RouteGuard requireAuth={true}>
    <KeepAliveTransition name={name}>
      <Component />
    </KeepAliveTransition>
  </RouteGuard>
);

// 包装不需要登录的页面
// const withPublic = (Component: React.ComponentType) => (
//   <RouteGuard requireAuth={false}>
//     <PageTransition>
//       <Component />
//     </PageTransition>
//   </RouteGuard>
// );

export default function Router() {
  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate to="/tiechu" replace />,
    },
    {
      path: '/home',
      element: withPageTransition(HomeLayout),
      children: [
        { path: 'study', element: <Study /> },
        { path: 'profile', element: <Profile /> },
        { index: true, element: <Navigate to="/home/profile" replace /> },
      ],
    },
    // 提供给app的页面
    {
      path: '/app-page',
      children: [
        {
          path: 'questionnaire-result',
          element: withPageTransition(QuestionnaireResult),
        },
      ],
    },
    // 活动页路由配置，后面所有的活动页都放到activePages文件里面
    {
      path: '/active-page',
      element: <ActivePagesLayout />,
      children: activePages,
    },
    // 个人信息 - 需要登录
    { path: '/personal-info', element: withAuth(PersonalInfo) },
    // 修改昵称 - 需要登录
    { path: '/change-nickname', element: withAuth(ChangeNickname) },
    // 客服中心 - 需要登录
    { path: '/call-center', element: withAuth(CallCenter) },
    // 意见反馈 - 需要登录
    { path: '/suggestion', element: withAuth(Suggestion) },
    // 地址列表 - 需要登录
    { path: '/address-list', element: withAuth(AddressList) },
    // 地址编辑 - 需要登录
    { path: '/address-edit', element: withAuth(AddressEdit) },
    // 设置 - 需要登录
    { path: '/setting', element: withAuth(Setting) },
    // 服务协议 - 不需要登录
    { path: '/protocol', element: withPageTransition(Protocol) },
    // 注销账号 - 需要登录
    { path: '/cancel-account', element: withAuth(CancelAccount) },
    // 注销账号确认 - 需要登录
    { path: '/cancel-account-confirm', element: withAuth(CancelAccountConfirm) },
    // 当前绑定手机号 - 需要登录
    { path: '/current-phone', element: withAuth(CurrentPhone) },
    // 当前绑定手机号 - 需要登录
    { path: '/study-calendar', element: withAuth(StudyCalendar) },
    { path: '/live-play', element: withAuth(LivePlay) },
    // 验证当前手机号 - 需要登录
    {
      path: '/verify-current-phone',
      element: withAuthAndKeepAlive(VerifyCurrentPhone, 'verify-current-phone'),
    },
    // 验证新手机号 - 需要登录
    {
      path: '/modify-phone',
      element: withAuth(ModifyPhone),
    },
    // 我的订单 - 需要登录
    { path: '/my-order', element: withAuthAndKeepAlive(MyOrder, 'my-order') },
    { path: '/order-detail', element: withAuth(OrderDetail) },
    { path: '/refund-record', element: withPageTransition(RefundRecord) },
    { path: '/logistics-detail', element: withPageTransition(Logistics) },
    // login - 不需要登录
    {
      path: '/login',
      element: withKeepAliveAndTransition(Login, 'login'),
    },
    { path: '/sms-verify', element: withPageTransition(SmsVerify) },
    // 修改密码 - 需要登录
    { path: '/modify-pwd', element: withAuth(ModifyPwd) },
    // 忘记密码 - 不需要登录
    { path: '/forget-pwd', element: withPageTransition(ForgetPwd) },
    // 续费课程优惠券 - 需要登录
    {
      path: '/renew-class-coupon',
      element: withKeepAliveAndTransition(RenewClassCoupon, 'renew-class-coupon'),
    },
    // 体验课领课 - 不需要登录
    { path: '/trial-course', element: withKeepAliveAndTransition(TrialCourse, 'trial-course') },
    // 下载APP - 不需要登录
    { path: '/download-app', element: withPageTransition(DownloadApp) },
    {
      path: '/printdesk/preset-audit',
      element: withPageTransition(PrintDeskPresetAudit),
    },
    {
      path: '/printdesk/feedback',
      element: withPageTransition(PrintDeskFeedbackList),
    },
    {
      path: '/printdesk/feedback/:id',
      element: withPageTransition(PrintDeskFeedbackDetail),
    },
    { path: '/course-detail', element: withPageTransition(CourseDetail) },
    { path: '/packageCourseDetail', element: withPageTransition(PackageCourseDetail) },
    { path: '/pay-confirm', element: withAuth(PayConfirm) },
    { path: '/pay', element: withAuth(Pay) },
    { path: '/pay-result', element: withPageTransition(PayResult) },
    // 自主约课相关页面 - 需要登录
    { path: '/book-study-by-self', element: withAuth(BookStudy) },
    { path: '/book-study-by-self/success', element: withAuth(BookStudySuccess) },
    // 铁杵成针 AI课堂（父级 Outlet 必须存在，否则 /tiechu/login、/tiechu/classroom 等子路由无法渲染）
    {
      path: '/tiechu',
      element: (
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      ),
      children: [
        { index: true, element: <Suspense><TieChuHome /></Suspense> },
        { path: 'login', element: <Suspense><TieChuStudentLogin /></Suspense> },
        { path: 'classroom/:classroomId', element: <Suspense><TieChuClassroom /></Suspense> },
        { path: 'classroom', element: <Suspense><TieChuClassroom /></Suspense> },
        { path: 'teacher', element: <Suspense><TieChuTeacher /></Suspense> },
      ],
    },
    { path: '/404', element: withPageTransition(NotFound) },
    { path: '/ali-pay-middle', element: withPageTransition(AliPayMiddle) },
    { path: '/helpPay', element: withPageTransition(HelpPay) },
    // 唤醒app页面 - 不需要登录
    { path: '/awaken-app', element: withPageTransition(AwakenApp) },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
  return (
    <RouteErrorBoundary>
      <Suspense>{routes}</Suspense>
    </RouteErrorBoundary>
  );
}
